import {
  RECORDER_INITIATED,
  SDK_RECORDING_CANCELED,
  SDK_RECORDING_COMPLETED,
  SDK_RECORD_BUTTON_CLICKED,
  SDK_UPLOAD_COMPLETED,
} from 'constants/events';
import * as analytics from 'utilities/analytics';
import { blockUnload, unblockUnload } from 'utilities/block-unload';
import { openExtensionIfInstalled } from 'utilities/extension';
import { incrementMetric } from 'utilities/metrics';
import getSDKInstance from 'utilities/recordSDKPersistentRecord';
import { isSdkSupported } from 'utilities/sdk-support/isSdkSupported';

import { CHROME_EXTENSION, DESKTOP, SDK } from './constants';

export const startDesktopRecording = source =>
  new Promise(resolve => {
    let recorderLaunched = false;

    const listener = () => {
      analytics.track(RECORDER_INITIATED, {
        source,
        recorder_type: DESKTOP,
      });
      incrementMetric('recorder.initiate', {
        recorder_type: DESKTOP,
        source,
      });
      recorderLaunched = true;

      resolve(true);
    };

    window.location.href = `loomDesktop://`;

    window.addEventListener('blur', listener);
    setTimeout(() => {
      window.removeEventListener('blur', listener);

      if (!recorderLaunched) {
        resolve(false);
      }
    }, 500);
  });

export const startChromeExtensionRecording = source =>
  new Promise(resolve => {
    openExtensionIfInstalled('persistent-record', success => {
      if (success) {
        analytics.track(RECORDER_INITIATED, {
          source,
          recorder_type: CHROME_EXTENSION,
        });
        incrementMetric('recorder.initiate', {
          recorder_type: CHROME_EXTENSION,
          source,
        });

        resolve(true);
      }

      resolve(false);
    });
  });

let sdkButton = null;

const setupSDK = async options => {
  if (sdkButton) {
    return;
  }

  const { configureButton } = await getSDKInstance().waitOnInstance();

  sdkButton = configureButton({
    hooks: {
      onStart: () => {
        options?.onStart?.();
        analytics.track(SDK_RECORD_BUTTON_CLICKED, { source: options.source });
        incrementMetric('recorder.start_recording', {
          recorder_type: SDK,
          source: options.source,
          isLoggedIn: options.isLoggedIn,
        });
      },
      onCancel: () => {
        options?.onCancel?.();
        analytics.track(SDK_RECORDING_CANCELED, { source: options.source });
        incrementMetric('recorder.cancel_recording', {
          recorder_type: SDK,
          source: options.source,
          isLoggedIn: options.isLoggedIn,
        });
      },
      onRecordingComplete: oembed => {
        blockUnload();
        options?.onRecordingComplete?.(oembed);
        window.open(oembed.sharedUrl + '?from_recorder=1', '_blank');
        analytics.track(SDK_RECORDING_COMPLETED, {
          source: options.source,
          videoUrl: oembed.sharedUrl,
          videoId: oembed.id,
        });
        incrementMetric('recorder.recording_complete', {
          recorder_type: SDK,
          source: options.source,
          isLoggedIn: options.isLoggedIn,
        });
      },
      onUploadComplete: oembed => {
        unblockUnload();
        options?.onUploadComplete?.(oembed);
        analytics.track(SDK_UPLOAD_COMPLETED, {
          source: options.source,
          videoUrl: oembed.sharedUrl,
          videoId: oembed.id,
        });
        incrementMetric('recorder.upload_complete', {
          recorder_type: SDK,
          source: options.source,
          isLoggedIn: options.isLoggedIn,
        });
      },
    },
  });
};

export const startSDKRecording = async options => {
  const { supported: isSDKSupported } = await isSdkSupported();

  if (isSDKSupported) {
    await setupSDK(options);
    analytics.track(RECORDER_INITIATED, {
      source: options.source,
      recorder_type: SDK,
    });
    incrementMetric('recorder.initiate', {
      recorder_type: SDK,
      source: options.source,
      isLoggedIn: options.isLoggedIn,
    });
    sdkButton.openPreRecordPanel();

    return true;
  }

  return false;
};
